import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useState,
} from 'react';
import authService from '../../services/authService';
import cognitoService from '../../services/cognitoService';

const AuthContext = createContext(null);

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  error: null,
  // New password challenge state
  newPasswordRequired: false,
  cognitoUser: null,
  userAttributes: null,
};

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true, error: null };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        newPasswordRequired: false,
        cognitoUser: null,
        userAttributes: null,
      };
    case 'LOGIN_FAILURE':
      return { ...state, loading: false, error: action.payload };
    case 'NEW_PASSWORD_REQUIRED':
      return {
        ...state,
        loading: false,
        newPasswordRequired: true,
        cognitoUser: action.payload.cognitoUser,
        userAttributes: action.payload.userAttributes,
      };
    case 'LOGOUT':
      return { ...initialState, loading: false };
    case 'SESSION_CHECKED':
      return { ...state, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [mockRole, setMockRole] = useState(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const result = await authService.getCurrentUser();
        dispatch({ type: 'LOGIN_SUCCESS', payload: result });
      } catch {
        dispatch({ type: 'SESSION_CHECKED' });
      }
    };
    checkSession();
  }, []);

  const login = useCallback(async (credentials) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await authService.login(credentials);

      // Check if Cognito returned a new-password challenge
      if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        dispatch({
          type: 'NEW_PASSWORD_REQUIRED',
          payload: {
            cognitoUser: response.cognitoUser,
            userAttributes: response.userAttributes,
          },
        });
        return;
      }

      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
    } catch (error) {
      const message = error.message || 'Login failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: message });
    }
  }, []);

  const completeNewPassword = useCallback(
    async (newPassword) => {
      dispatch({ type: 'LOGIN_START' });
      try {
        // Strip read-only attributes that Cognito rejects
        const readOnlyAttrs = [
          'email',
          'email_verified',
          'phone_number',
          'phone_number_verified',
          'sub',
          'identities',
        ];
        const attrs = { ...state.userAttributes };
        readOnlyAttrs.forEach((key) => delete attrs[key]);

        const response = await cognitoService.completeNewPassword(
          state.cognitoUser,
          newPassword,
          attrs
        );
        dispatch({ type: 'LOGIN_SUCCESS', payload: response });
      } catch (error) {
        const message = error.message || 'Failed to set new password';
        dispatch({ type: 'LOGIN_FAILURE', payload: message });
      }
    },
    [state.cognitoUser, state.userAttributes]
  );

  const logout = useCallback(async () => {
    await authService.logout();
    dispatch({ type: 'LOGOUT' });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        user: state.user
          ? { ...state.user, role: mockRole || state.user.role }
          : state.user,
        originalRole: state.user?.role || null,
        mockRole,
        setMockRole,
        login,
        logout,
        clearError,
        completeNewPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
